<script lang="ts">
  import CoordinatorWorkspace from './CoordinatorWorkspace.svelte';
  import QueryStatus from '$lib/components/ui/QueryStatus.svelte';
  import { formatIST, todayIST } from '$lib/utils/dates';
  import { untrack } from 'svelte';
  import { scheduleUndo, confirmAction } from '$lib/state/actionDialog.svelte';
  import { route } from '$lib/state/route.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import {
    Trash2,
    PhoneCall,
    Phone,
    CalendarClock,
    CheckCircle2,
    XCircle,
    Mail,
    ArrowDownLeft,
    ArrowUpRight,
    ArrowUpDown,
    ChevronRight
  } from 'lucide-svelte';
  import { activitiesApi, companiesApi, peopleApi, dealsApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { apiFetch, getErrorMessage } from '$lib/api/client';
  import SearchBar from '$lib/components/ui/SearchBar.svelte';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import CustomFieldsForm from '$lib/components/ui/CustomFieldsForm.svelte';
  import EmailActivityModal from '$lib/components/entities/EmailActivityModal.svelte';
  import Composer from './Composer.svelte';
  import FilterChipRow from '$lib/components/ui/FilterChipRow.svelte';
  import { groupEmailThreads, displayRowKey, newestMessage, sortMessagesDesc, threadParticipants } from '$lib/utils/emailThreads';
  import type { Activity, ActivityType, ActivityStatus, Person } from '$lib/types';

  const queryClient = useQueryClient();
  const selectedId = $derived(route.current.tab === 'activities' ? route.current.id : undefined);
  const recordQuery = createQuery(() => ({
    queryKey: ['activity', selectedId], queryFn: () => activitiesApi.get(selectedId!), enabled: selectedId !== undefined
  }));
  $effect(() => {
    const id = selectedId, row = recordQuery.data;
    untrack(() => {
      if (row && row.id === id && (row.type === 'email' ? emailDetailRow?.id !== id : !showForm || editingId !== id)) openRow(row);
      else if (!id) { if (editingId !== null) showForm = false; emailDetailRow = null; }
    });
  });
  let completing = $state<Activity | null>(null);

  const typeChips: Array<{ value: ActivityType | ''; label: string }> = [
    { value: '', label: 'All' },
    { value: 'note', label: 'Notes' },
    { value: 'task', label: 'Tasks' },
    { value: 'cold_call', label: 'Cold calls' },
    { value: 'call', label: 'Calls' },
    { value: 'meeting', label: 'Meetings' },
    { value: 'email', label: 'Emails' }
  ];

  // ponytail: same tint recipe as LeadDetail's activity timeline in the mockup.
  const TYPE_BADGE: Record<string, { bg: string; fg: string }> = {
    note: { bg: '#E3F0F1', fg: '#226A73' },
    task: { bg: '#E6E9EE', fg: '#10223A' },
    cold_call: { bg: '#FBF0DC', fg: '#8a5c00' },
    call: { bg: '#FBF0DC', fg: '#8a5c00' },
    meeting: { bg: '#ECF8CC', fg: '#5c7a00' },
    email: { bg: '#EAE6F3', fg: '#5a4b8a' }
  };
  function typeBadge(t: string) {
    return TYPE_BADGE[t] ?? TYPE_BADGE.note;
  }

  const STATUS_BADGE: Record<string, { bg: string; fg: string }> = {
    scheduled: { bg: '#E3F0F1', fg: '#226A73' },
    completed: { bg: '#ECF8CC', fg: '#5c7a00' },
    cancelled: { bg: '#E6E9EE', fg: '#666666' },
    no_show: { bg: '#FBE7E4', fg: '#C0392B' }
  };
  function statusBadge(s: string) {
    return STATUS_BADGE[s] ?? STATUS_BADGE.completed;
  }

  let search = $state('');
  let page = $state(1);
  let type = $state<ActivityType | ''>('');
  // "Upcoming" tab: status=scheduled, ordered soonest-first by the backend.
  let upcomingOnly = $state(false);
  // Date sort toggle — desc (newest first) is the default; meaningless (and
  // hidden) on the Upcoming tab, which is always soonest-first server-side.
  let order = $state<'asc' | 'desc'>('desc');
  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  // A planned item is completed through the composer, never by editing status.
  let editingStatus = $state('');
  let formError = $state('');

  const verticalId = $derived(
    session.selectedVertical === 'all' ? undefined : session.selectedVertical
  );
  function emptyForm() {
    return {
      vertical_id: verticalId ?? session.me?.verticals[0]?.id,
      type: 'note' as ActivityType,
      company_id: undefined as number | undefined,
      person_id: undefined as number | undefined,
      deal_id: undefined as number | undefined,
      title: '',
      body: '',
      outcome: '',
      status: '' as ActivityStatus | '',
      due_at: '',
      scheduled_at: '',
      assignee_user_id: null as number | null,
      location: '',
      next_action: '',
      next_action_date: '',
      custom: {} as Record<string, unknown>
    };
  }
  let form = $state(emptyForm());
  const customFieldDefs = $derived(session.customFieldsFor('activities', form.vertical_id));
  type TeamMember = { id: number; name: string };
  const assignees = createQuery(() => ({
    queryKey: ['activity-assignees', form.vertical_id],
    queryFn: () => apiFetch<TeamMember[]>(`/activities/assignees?vertical_id=${form.vertical_id}`),
    enabled: showForm && !!form.vertical_id
  }));
  function ownerName(row: Activity) {
    return row.assignee_user_id ? rowOwners.data?.find((u) => u.id === row.assignee_user_id)?.name || `Team member #${row.assignee_user_id}` : 'Unassigned';
  }
  function localDateTime(value: string) {
    const d = new Date(value);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }
  function refreshActivities() {
    for (const key of ['activities', 'people', 'person-detail', 'person-activities']) queryClient.invalidateQueries({ queryKey: [key] });
  }

  const listQuery = createQuery(() => ({
    queryKey: ['activities', { search, page, type, verticalId, upcomingOnly, order }],
    queryFn: () =>
      activitiesApi.list({
        q: search || undefined,
        page,
        per_page: 25,
        type: type || undefined,
        vertical_id: verticalId,
        status: upcomingOnly ? 'scheduled' : undefined,
        order
      })
  }));

  const rowVerticals = $derived([...new Set(listQuery.data?.results.map((a) => a.vertical_id) ?? [])].sort());
  const rowOwners = createQuery(() => ({
    queryKey: ['activity-owner-names', rowVerticals],
    queryFn: async () => (await Promise.all(rowVerticals.map((id) => apiFetch<TeamMember[]>(`/activities/assignees?vertical_id=${id}`)))).flat(),
    enabled: rowVerticals.length > 0
  }));

  const companiesQuery = createQuery(() => ({
    queryKey: ['companies-picker', { verticalId }],
    queryFn: () => companiesApi.list({ per_page: 100, vertical_id: verticalId })
  }));
  const peopleQuery = createQuery(() => ({
    queryKey: ['people-picker', { verticalId }],
    queryFn: () => peopleApi.list({ per_page: 100, vertical_id: verticalId })
  }));
  const dealsQuery = createQuery(() => ({
    queryKey: ['deals-picker', { verticalId }],
    queryFn: () => dealsApi.list({ per_page: 100, vertical_id: verticalId })
  }));

  $effect(() => {
    search;
    type;
    verticalId;
    upcomingOnly;
    order;
    page = 1;
  });

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? activitiesApi.update(editingId, body) : activitiesApi.create(body),
    onSuccess: () => {
      refreshActivities();
      closeForm();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => activitiesApi.remove(id),
    onError: (err: unknown) => toast.error(getErrorMessage(err)),
    onSuccess: refreshActivities
  }));

  // Quick status transitions for a scheduled item: no separate form, just an
  // inline prompt for the outcome note (completed) or a plain confirm
  // (cancelled) — same weight as the existing delete confirm() below.
  const statusMutation = createMutation(() => ({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      activitiesApi.update(id, body),
    onSuccess: refreshActivities,
    onError: (err: unknown) => { formError = getErrorMessage(err); toast.error(formError); }
  }));

  function markCompleted(row: Activity, e: MouseEvent) {
    e.stopPropagation();
    completing = row;
  }

  async function cancelScheduled(row: Activity, e: MouseEvent) {
    e.stopPropagation();
    if (!await confirmAction(`Cancel “${row.title || row.type}”?`)) return;
    statusMutation.mutate({ id: row.id, body: { status: 'cancelled' } });
  }

  function openCreate() {
    if (selectedId) route.push({ tab: 'activities' });
    editingId = null;
    editingStatus = '';
    form = emptyForm();
    if (type) form.type = type as ActivityType;
    formError = '';
    showForm = true;
  }

  // Quick-log shortcuts: same create form, just pre-set to the type a rep
  // is most likely logging in the moment rather than picking it from the
  // dropdown first.
  function quickLog(t: ActivityType) {
    editingId = null;
    editingStatus = '';
    form = emptyForm();
    form.type = t;
    formError = '';
    showForm = true;
  }

  function openEdit(row: Activity) {
    if (selectedId !== row.id) route.push({ tab: 'activities', id: row.id });
    editingId = row.id;
    editingStatus = row.status;
    form = {
      vertical_id: row.vertical_id,
      type: row.type,
      company_id: row.company_id ?? undefined,
      person_id: row.person_id ?? undefined,
      deal_id: row.deal_id ?? undefined,
      title: row.title,
      body: row.body,
      outcome: row.outcome,
      status: row.status as ActivityStatus | '',
      due_at: row.due_at ? row.due_at.slice(0, 10) : '',
      // Edit in local time; submit an explicit UTC instant.
      scheduled_at: row.scheduled_at ? localDateTime(row.scheduled_at) : '',
      assignee_user_id: row.assignee_user_id ?? null,
      location: row.location ?? '',
      next_action: row.next_action,
      next_action_date: row.next_action_date ? row.next_action_date.slice(0, 10) : '',
      custom: { ...row.custom }
    };
    formError = '';
    showForm = true;
  }

  function closeForm() {
    showForm = false;
    if (selectedId) route.push({ tab: 'activities' });
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    formError = '';
    saveMutation.mutate({
      ...form,
      company_id: form.company_id ?? null,
      person_id: form.person_id ?? null,
      deal_id: form.deal_id ?? null,
      due_at: form.due_at || null,
      scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
      next_action_date: form.next_action_date || null
    });
  }

  function remove(row: Activity) {
    // Deleting an `email` activity is a dismiss — see the DELETE handler's
    // dismissed_emails bookkeeping — so it gets its own wording.
    const prompt =
      row.type === 'email'
        ? `Dismiss "${row.title || 'this email'}" as not relevant? It will not be re-logged.`
        : `Delete "${row.title || row.type}"?`;
    scheduleUndo(prompt.replace(/\?$/, ''), () => deleteMutation.mutate(row.id));
  }

  function companyName(id: number | null): string {
    if (!id) return '';
    return companiesQuery.data?.results.find((c) => c.id === id)?.name ?? '';
  }

  function domainOf(addr: string): string {
    return addr.split('@')[1]?.toLowerCase().trim() ?? '';
  }

  // Cheap direction hint for an email touchpoint: if the From address is a
  // known person or company contact's, it arrived from them (received);
  // otherwise it was sent from our side. No mailbox/folder round trip
  // needed.
  function emailDirection(row: Activity): 'received' | 'sent' {
    const from = row.email?.from_addr?.toLowerCase() ?? '';
    const person = row.person_id
      ? peopleQuery.data?.results.find((p: Person) => p.id === row.person_id)
      : undefined;
    if (person?.email && person.email.toLowerCase() === from) return 'received';
    const company = row.company_id
      ? companiesQuery.data?.results.find((c) => c.id === row.company_id)
      : undefined;
    if (company?.email && domainOf(company.email) === domainOf(from)) return 'received';
    return 'sent';
  }

  // Thread grouping (build brief item 3): consecutive `email` rows sharing a
  // thread_key collapse into one expandable card. See emailThreads.ts —
  // grouping runs over whatever order the list is currently sorted in.
  const displayRows = $derived(groupEmailThreads(listQuery.data?.results ?? []));
  let expandedThreads = $state<Set<string>>(new Set());
  function toggleThread(key: string) {
    const next = new Set(expandedThreads);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    expandedThreads = next;
  }

  let emailDetailRow = $state<Activity | null>(null);

  function openRow(row: Activity) {
    if (selectedId !== row.id) route.push({ tab: 'activities', id: row.id });
    if (row.type === 'email') {
      emailDetailRow = row;
    } else {
      openEdit(row);
    }
  }

  // Follow-up chip on list rows: overdue means the next-action date is
  // strictly before today (date-only comparison, ignoring time-of-day).
  function isOverdue(dateStr: string): boolean {
    return dateStr.slice(0, 10) < todayIST();
  }

  // Consumes the Header's "+ New touchpoint" signal (see session.svelte.ts's
  // PageSignal doc). Topbar search never targets activities, so only the
  // create mode is relevant here.
  $effect(() => {
    const signal = session.consumeSignal('activities');
    if (signal?.mode === 'create') openCreate();
  });
</script>

<div class="tym-page">
  <CoordinatorWorkspace />
  {#if recordQuery.isError}<p class="tym-error-state">{getErrorMessage(recordQuery.error)} <button type="button" class="btn-secondary" onclick={() => recordQuery.refetch()}>Retry</button></p>{/if}
  {#if completing}
    <!-- Completion records what happened and the next step through the shared
         composer (POST /activities/:id/complete); a bare status PUT is refused. -->
    <Composer activity={completing} mode="log" onClose={() => { completing = null; }} onSaved={refreshActivities} />
  {/if}
  {#if formError && !showForm}<p class="text-sm text-red-600" role="alert">{formError}</p>{/if}
  <div class="mb-2.5 flex flex-wrap items-center gap-2">
    <button
      type="button"
      class="tym-chip {!upcomingOnly ? 'active' : ''}"
      onclick={() => (upcomingOnly = false)}
    >
      All
    </button>
    <button
      type="button"
      class="tym-chip {upcomingOnly ? 'active' : ''}"
      onclick={() => (upcomingOnly = true)}
    >
      Upcoming meetings / calls
    </button>
  </div>

  <FilterChipRow
    chips={typeChips.map((c) => ({ id: c.value, label: c.label }))}
    activeId={type}
    onSelect={(id) => (type = id as ActivityType | '')}
    ariaLabel="Filter touchpoints by type"
    class="mb-4"
  />

  <div class="mb-3 flex flex-wrap gap-2">
    <button type="button" class="btn-secondary" onclick={() => quickLog('cold_call')}>
      <PhoneCall class="h-4 w-4" /> Log cold call
    </button>
    <button type="button" class="btn-secondary" onclick={() => quickLog('call')}>
      <Phone class="h-4 w-4" /> Log call
    </button>
    <button type="button" class="btn-secondary" onclick={() => quickLog('meeting')}>
      <CalendarClock class="h-4 w-4" /> Log meeting
    </button>
  </div>

  <div class="flex flex-wrap items-start justify-between gap-3">
    <SearchBar bind:value={search} onCreate={openCreate} createLabel="New touchpoint" />
    {#if !upcomingOnly}
      <button
        type="button"
        class="btn-secondary"
        onclick={() => (order = order === 'desc' ? 'asc' : 'desc')}
        title="Toggle sort order"
      >
        <ArrowUpDown class="h-4 w-4" />
        {order === 'desc' ? 'Newest first' : 'Oldest first'}
      </button>
    {/if}
  </div>

  {#snippet activityRow(row: Activity)}
    {@const badge = typeBadge(row.type)}
    {@const sBadge = statusBadge(row.status)}
    {@const isEmail = row.type === 'email'}
    <tr class="clickable" onclick={() => openRow(row)}>
      <td>
        <div class="flex items-center gap-1.5">
          {#if isEmail}<Mail class="h-3.5 w-3.5 shrink-0" style="color:{badge.fg}" />{/if}
          <span class="tym-badge capitalize" style="background:{badge.bg}; color:{badge.fg}"
            >{row.type.replace('_', ' ')}</span
          >
          {#if isEmail}
            <span class="tym-badge" style="background:#E6E9EE; color:#666" title="Auto-logged from your mailbox"
              >Synced</span
            >
          {/if}
        </div>
      </td>
      <td class="font-semibold text-navy">
        {row.title}
        {#if row.type === 'meeting' || row.type === 'call'}
          <div class="mt-1 text-xs font-normal text-dark-gray">Owner: {ownerName(row)}{#if row.location} · {row.location}{/if}</div>
        {/if}
        {#if isEmail && row.email}
          <div class="mt-0.5 flex items-center gap-1 text-[11.5px] font-normal text-dark-gray">
            {#if emailDirection(row) === 'received'}
              <ArrowDownLeft class="h-3 w-3 shrink-0" />
            {:else}
              <ArrowUpRight class="h-3 w-3 shrink-0" />
            {/if}
            <span class="truncate">{row.email.from_addr}</span>
          </div>
          {#if row.email.snippet}
            <p class="mt-0.5 line-clamp-1 text-[11.5px] font-normal text-dark-gray">{row.email.snippet}</p>
          {/if}
        {/if}
      </td>
      <td class="muted">{companyName(row.company_id)}</td>
      <td class="muted">
        {#if isEmail && row.email?.sent_at}
          {row.email.sent_at.slice(0, 16).replace('T', ' ')}
        {:else if row.scheduled_at}
          Scheduled: {formatIST(row.scheduled_at)}
        {:else if row.due_at}
          Due: {row.due_at.slice(0, 10)}
        {/if}
      </td>
      <td>
        {#if row.status}
          <span
            class="tym-badge capitalize"
            style="background:{sBadge.bg}; color:{sBadge.fg}"
          >
            {row.status.replace('_', ' ')}
          </span>
        {/if}
      </td>
      <td>
        {#if row.next_action_date}
          <span
            class="tym-badge"
            style={isOverdue(row.next_action_date)
              ? 'background:#FBE7E4; color:#C0392B'
              : 'background:#E3F0F1; color:#226A73'}
          >
            Follow up: {row.next_action_date.slice(0, 10)}
          </span>
        {/if}
      </td>
      <td class="muted">{row.created_by}</td>
      <td class="num">
        <div class="flex justify-end gap-1.5">
          {#if row.status === 'scheduled'}
            <button
              type="button"
              class="btn-secondary !h-10 !w-10 !p-0"
              onclick={(e) => markCompleted(row, e)}
              aria-label={`Mark ${row.title || row.type} completed`}
            >
              <CheckCircle2 class="h-4 w-4" />
            </button>
            <button
              type="button"
              class="btn-secondary !h-10 !w-10 !p-0"
              onclick={(e) => cancelScheduled(row, e)}
              aria-label={`Cancel ${row.title || row.type}`}
            >
              <XCircle class="h-4 w-4" />
            </button>
          {/if}
          <button
            type="button"
            class="btn-danger !h-10 !w-10 !p-0"
            onclick={(e) => {
              e.stopPropagation();
              remove(row);
            }}
            aria-label={isEmail
              ? `Dismiss ${row.title || 'email'} as not relevant`
              : `Delete ${row.title || row.type}`}
          >
            <Trash2 class="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  {/snippet}

  {#snippet activityCard(row: Activity)}
    {@const badge = typeBadge(row.type)}
    {@const sBadge = statusBadge(row.status)}
    {@const isEmail = row.type === 'email'}
    <div class="tym-list-card !cursor-default">
      <button type="button" class="block w-full text-left" onclick={() => openRow(row)}>
        <span class="flex items-center gap-1.5">
          {#if isEmail}<Mail class="h-3.5 w-3.5 shrink-0" style="color:{badge.fg}" />{/if}
          <span class="tym-badge capitalize" style="background:{badge.bg}; color:{badge.fg}"
            >{row.type.replace('_', ' ')}</span
          >
          {#if isEmail}
            <span class="tym-badge" style="background:#E6E9EE; color:#666">Synced</span>
          {/if}
          <span class="flex-1"></span>
          <span class="tym-tabular shrink-0 text-[11px] text-dark-gray">
            {#if isEmail && row.email?.sent_at}
              {row.email.sent_at.slice(0, 16).replace('T', ' ')}
            {:else if row.scheduled_at}
              {formatIST(row.scheduled_at)}
            {:else if row.due_at}
              Due {row.due_at.slice(0, 10)}
            {:else}
              {row.created_at.slice(0, 10)}
            {/if}
          </span>
        </span>
        <span class="mt-2 block text-[15px] font-semibold leading-[1.35] text-navy">{row.title || '(untitled)'}</span>
        {#if row.type === 'meeting' || row.type === 'call'}
          <span class="mt-1 block text-xs text-dark-gray">Owner: {ownerName(row)}{#if row.location} · {row.location}{/if}</span>
        {/if}
        {#if isEmail && row.email}
          <span class="mt-1 flex items-center gap-1 text-[11.5px] text-dark-gray">
            {#if emailDirection(row) === 'received'}
              <ArrowDownLeft class="h-3 w-3 shrink-0" />
            {:else}
              <ArrowUpRight class="h-3 w-3 shrink-0" />
            {/if}
            <span class="truncate">{row.email.from_addr}</span>
          </span>
          {#if row.email.snippet}
            <span class="mt-1 line-clamp-2 block text-[12px] text-dark-gray">{row.email.snippet}</span>
          {/if}
        {:else if row.body}
          <span class="mt-1 line-clamp-2 block text-[12px] text-dark-gray">{row.body}</span>
        {/if}
        {#if row.status || row.next_action_date}
          <span class="mt-2 flex flex-wrap items-center gap-1.5">
            {#if row.status}
              <span class="tym-badge capitalize" style="background:{sBadge.bg}; color:{sBadge.fg}"
                >{row.status.replace('_', ' ')}</span
              >
            {/if}
            {#if row.next_action_date}
              <span
                class="tym-badge"
                style={isOverdue(row.next_action_date)
                  ? 'background:#FBE7E4; color:#C0392B'
                  : 'background:#E3F0F1; color:#226A73'}
              >
                Follow up {row.next_action_date.slice(0, 10)}
              </span>
            {/if}
          </span>
        {/if}
        {#if companyName(row.company_id) || row.created_by}
          <span class="mt-2 block truncate text-[11.5px] text-dark-gray">
            {[companyName(row.company_id), row.created_by].filter(Boolean).join(' · ')}
          </span>
        {/if}
      </button>
      <div class="mt-3 flex gap-2 border-t border-[#F0F0F0] pt-3">
        {#if row.status === 'scheduled'}
          <button type="button" class="btn-secondary flex-1" onclick={(e) => markCompleted(row, e)}>
            <CheckCircle2 class="h-4 w-4" /> Complete
          </button>
          <button type="button" class="btn-secondary flex-1" onclick={(e) => cancelScheduled(row, e)}>
            <XCircle class="h-4 w-4" /> Cancel
          </button>
        {:else}
          <span class="flex-1"></span>
        {/if}
        <button
          type="button"
          class="btn-danger"
          onclick={() => remove(row)}
          aria-label={isEmail ? `Dismiss ${row.title || 'email'} as not relevant` : `Delete ${row.title || row.type}`}
        >
          <Trash2 class="h-4 w-4" />
        </button>
      </div>
    </div>
  {/snippet}

  {#if listQuery.isLoading}
    <QueryStatus loading label="touchpoints" />
  {:else if listQuery.isError}
    <QueryStatus error={listQuery.error} label="touchpoints" onRetry={() => listQuery.refetch()} />
  {:else if listQuery.data}
    <!-- desktop/tablet table; below `sm` an 8-column table is unreadable and
         gives way to the card list further down -->
    <div class="tym-table-wrap tym-shadow hidden sm:block">
      <div class="tym-table-scroll">
        <table class="tym-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Title</th>
              <th>Company</th>
              <th>Date</th>
              <th>Status</th>
              <th>Follow up</th>
              <th>Created by</th>
              <th class="num"></th>
            </tr>
          </thead>
          <tbody>
            {#each displayRows as entry (displayRowKey(entry))}
              {#if entry.kind === 'thread' && entry.messages.length > 1}
                {@const newest = newestMessage(entry.messages)}
                {@const open = expandedThreads.has(entry.key)}
                <tr class="clickable" onclick={() => toggleThread(entry.key)}>
                  <td colspan="8" class="!p-0">
                    <div class="flex min-h-11 w-full items-center gap-3 px-[18px] py-3">
                      <ChevronRight class="h-4 w-4 shrink-0 text-dark-gray transition-transform {open ? 'rotate-90' : ''}" />
                      <Mail class="h-4 w-4 shrink-0" style="color:{typeBadge('email').fg}" />
                      <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-center gap-2">
                          <span class="truncate font-semibold text-navy">{newest.email?.subject || newest.title || '(no subject)'}</span>
                          <span class="tym-badge shrink-0" style="background:#EAE6F3; color:#5a4b8a"
                            >{entry.messages.length} messages</span
                          >
                        </div>
                        <div class="mt-0.5 truncate text-[11.5px] font-normal text-dark-gray">{threadParticipants(entry.messages)}</div>
                      </div>
                      <span class="shrink-0 text-[11.5px] text-dark-gray">
                        {(newest.email?.sent_at ?? newest.created_at).slice(0, 16).replace('T', ' ')}
                      </span>
                    </div>
                  </td>
                </tr>
                {#if open}
                  {#each sortMessagesDesc(entry.messages) as row (row.id)}
                    {@render activityRow(row)}
                  {/each}
                {/if}
              {:else}
                {@render activityRow(entry.kind === 'thread' ? entry.messages[0] : entry.activity)}
              {/if}
            {:else}
              <tr><td class="empty" colspan="8">No touchpoints found.</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <!-- mobile card list — same thread grouping as the table above -->
    <div class="flex flex-col gap-2.5 sm:hidden">
      {#each displayRows as entry (displayRowKey(entry))}
        {#if entry.kind === 'thread' && entry.messages.length > 1}
          {@const newest = newestMessage(entry.messages)}
          {@const open = expandedThreads.has(entry.key)}
          <button type="button" class="tym-list-card" onclick={() => toggleThread(entry.key)}>
            <div class="flex items-center gap-2">
              <ChevronRight
                class="h-4 w-4 shrink-0 text-dark-gray transition-transform {open ? 'rotate-90' : ''}"
              />
              <Mail class="h-4 w-4 shrink-0" style="color:{typeBadge('email').fg}" />
              <span class="tym-badge shrink-0" style="background:#EAE6F3; color:#5a4b8a"
                >{entry.messages.length} messages</span
              >
              <span class="flex-1"></span>
              <span class="tym-tabular shrink-0 text-[11px] text-dark-gray">
                {(newest.email?.sent_at ?? newest.created_at).slice(0, 10)}
              </span>
            </div>
            <div class="mt-2 text-[15px] font-semibold leading-[1.35] text-navy">
              {newest.email?.subject || newest.title || '(no subject)'}
            </div>
            <div class="mt-1 truncate text-[11.5px] text-dark-gray">{threadParticipants(entry.messages)}</div>
          </button>
          {#if open}
            <div class="flex flex-col gap-2.5 border-l-2 border-[#EAE6F3] pl-2.5">
              {#each sortMessagesDesc(entry.messages) as row (row.id)}
                {@render activityCard(row)}
              {/each}
            </div>
          {/if}
        {:else}
          {@render activityCard(entry.kind === 'thread' ? entry.messages[0] : entry.activity)}
        {/if}
      {:else}
        <p class="tym-empty-state">No touchpoints found.</p>
      {/each}
    </div>

    <Pagination
      page={listQuery.data.page}
      perPage={listQuery.data.per_page}
      count={listQuery.data.count}
      onChange={(p) => (page = p)}
    />
  {/if}
</div>

{#if emailDetailRow}
  <EmailActivityModal
    activity={emailDetailRow}
    onClose={() => { emailDetailRow = null; if (selectedId) route.push({ tab: 'activities' }); }}
    onDismissed={() => { emailDetailRow = null; if (selectedId) route.push({ tab: 'activities' }); }}
  />
{/if}

{#if showForm}
  <Modal title={editingId ? 'Edit touchpoint' : 'New touchpoint'} onClose={closeForm} wide>
    <form id="activity-form" class="space-y-4" onsubmit={submit}>
      {#if session.me && session.me.verticals.length > 1}
        <div>
          <label class="label" for="vertical">Vertical</label>
          <select id="vertical" class="input" bind:value={form.vertical_id} disabled={!!editingId} onchange={() => { form.assignee_user_id = null; }}>
            {#each session.me.verticals as v (v.id)}
              <option value={v.id}>{v.name}</option>
            {/each}
          </select>
        </div>
      {/if}
      <div>
        <label class="label" for="type">Type</label>
        <select id="type" class="input" bind:value={form.type}>
          <option value="note">Note</option>
          <option value="task">Task</option>
          <option value="cold_call">Cold call</option>
          <option value="call">Call</option>
          <option value="meeting">Meeting</option>
        </select>
      </div>
      {#if form.type === 'meeting' || form.type === 'call'}
        <div>
          <label class="label" for="meeting-owner">Our team member (optional)</label>
          <select id="meeting-owner" class="input" bind:value={form.assignee_user_id}>
            <option value={null}>Unassigned</option>
            {#each assignees.data ?? [] as member (member.id)}<option value={member.id}>{member.name}</option>{/each}
          </select>
          {#if assignees.isError}<p class="text-sm text-red-600">{getErrorMessage(assignees.error)}</p>{/if}
          <label class="label" for="meeting-location">Venue / call link</label>
          <input id="meeting-location" class="input" bind:value={form.location} />
        </div>
        <div>
          <label class="label" for="scheduled_at">Scheduled for (leave blank to log retrospectively)</label>
          <input
            id="scheduled_at"
            type="datetime-local"
            class="input"
            bind:value={form.scheduled_at}
          />
        </div>
      {/if}
      <div>
        <label class="label" for="title">Title</label>
        <input id="title" class="input" bind:value={form.title} />
      </div>
      <div>
        <label class="label" for="body">Body</label>
        <textarea id="body" rows="4" class="input" bind:value={form.body}></textarea>
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label class="label" for="company">Company</label>
          <select id="company" class="input" bind:value={form.company_id}>
            <option value={undefined}>-- none --</option>
            {#each companiesQuery.data?.results ?? [] as c (c.id)}
              <option value={c.id}>{c.name}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="label" for="person">Person</label>
          <select id="person" class="input" bind:value={form.person_id}>
            <option value={undefined}>-- none --</option>
            {#each peopleQuery.data?.results ?? [] as p (p.id)}
              <option value={p.id}>{p.first_name} {p.last_name}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="label" for="deal">Deal</label>
          <select id="deal" class="input" bind:value={form.deal_id}>
            <option value={undefined}>-- none --</option>
            {#each dealsQuery.data?.results ?? [] as d (d.id)}
              <option value={d.id}>{d.name}</option>
            {/each}
          </select>
        </div>
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="due_at">Due</label>
          <input id="due_at" type="date" class="input" bind:value={form.due_at} />
        </div>
        <div>
          <label class="label" for="status">Status</label>
          <select id="status" class="input" bind:value={form.status}>
            <option value="">-- auto --</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed" disabled={editingStatus === 'scheduled'}>Completed{editingStatus === 'scheduled' ? ' — use the ✓ button to log the outcome' : ''}</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No-show</option>
          </select>
        </div>
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="next_action">Next action</label>
          <input id="next_action" class="input" bind:value={form.next_action} />
        </div>
        <div>
          <label class="label" for="next_action_date">Next action date</label>
          <input id="next_action_date" type="date" class="input" bind:value={form.next_action_date} />
        </div>
      </div>
      <div>
        <label class="label" for="outcome">Outcome</label>
        <input id="outcome" class="input" bind:value={form.outcome} />
      </div>

      <CustomFieldsForm defs={customFieldDefs} values={form.custom} />

      {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}
    </form>

    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
        <!-- sibling of the <form>, so it submits via the `form` attribute -->
        <button type="submit" form="activity-form" class="btn-primary" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}
